import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../../components/layout";
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Modal,
  Calendar,
  Badge,
  Tag,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { useNavigate } from "react-router-dom";
import moment from "moment";

/* ── Design tokens ─────────────────────────────────────────── */
const EMERALD    = "#034640ff";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ── Styles injected once ───────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("doctor-home-styles")) return;
  const el = document.createElement("style");
  el.id = "doctor-home-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .dh-root { font-family: 'DM Sans', sans-serif; }

    /* ── Hero banner ── */
    .dh-hero {
      position: relative; overflow: hidden;
      background: linear-gradient(135deg, ${EMERALD} 50%, ${TEAL} 100%);
      border-radius: 20px; padding: 32px 36px; margin-bottom: 32px;
      color: ${WHITE};
    }
    .dh-hero-blob {
      position: absolute; top: -80px; right: -80px;
      width: 320px; height: 320px; border-radius: 50%;
      background: rgba(255,255,255,0.08); pointer-events: none;
    }
    .dh-hero-blob-2 {
      position: absolute; bottom: -60px; left: 40%;
      width: 200px; height: 200px; border-radius: 50%;
      background: rgba(255,255,255,0.06); pointer-events: none;
    }
    .dh-hero-title {
      font-family: 'Sora', sans-serif; font-size: 26px;
      font-weight: 700; margin: 0 0 6px; letter-spacing: -0.3px;
    }
    .dh-hero-sub {
      font-size: 14px; opacity: 0.85; margin: 0;
      color: ${WHITE};
    }
    p{
     color: ${WHITE}!important;
    }
    .dh-hero-actions { display: flex; gap: 10px; margin-top: 20px; flex-wrap: wrap; }
    .dh-hero-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 9px 20px; border-radius: 10px; font-size: 13px;
      font-weight: 600; font-family: 'DM Sans', sans-serif;
      cursor: pointer; border: none; transition: all 0.2s;
    }
    .dh-hero-btn-primary {
      background: ${WHITE}; color: ${EMERALD};
      box-shadow: 0 4px 14px rgba(0,0,0,0.12);
    }
    .dh-hero-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0,0,0,0.16); }
    .dh-hero-btn-ghost {
      background: rgba(255,255,255,0.15); color: ${WHITE};
      border: 1.5px solid rgba(255,255,255,0.35);
    }
    .dh-hero-btn-ghost:hover { background: rgba(255,255,255,0.22); }

    /* ── Stat cards ── */
    .dh-stat-card {
      border-radius: 14px !important;
      border: 1.5px solid #e2e8f0 !important;
      box-shadow: none !important;
      transition: transform 0.22s, box-shadow 0.22s, border-color 0.22s !important;
      cursor: default;
    }
    .dh-stat-card:hover {
      transform: translateY(-3px) !important;
      box-shadow: 0 12px 32px rgba(13,148,136,0.13) !important;
      border-color: ${TEAL} !important;
    }
    .dh-stat-icon {
      width: 40px; height: 40px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      font-size: 18px; margin-bottom: 12px;
    }

    /* ── Section eyebrow ── */
    .dh-eyebrow {
      display: inline-block; font-family: 'Sora', sans-serif;
      font-size: 10px; font-weight: 700; letter-spacing: 2px;
      text-transform: uppercase; color: ${EMERALD};
      padding: 3px 10px; border-radius: 20px;
      background: ${TEAL_LIGHT}; margin-bottom: 10px;
    }

    /* ── Table card ── */
    .dh-table-card {
      border-radius: 16px !important;
      border: 1.5px solid #e2e8f0 !important;
      box-shadow: none !important;
    }
    .dh-table-card .ant-card-head {
      border-bottom: 1.5px solid #e2e8f0;
      padding: 0 24px;
    }
    .dh-table-card .ant-card-head-title {
      font-family: 'Sora', sans-serif; font-weight: 600;
      font-size: 15px; color: ${SLATE};
    }
    .dh-table-card .ant-table-thead > tr > th {
      background: ${SURFACE} !important;
      font-size: 11px; font-weight: 700; letter-spacing: 0.8px;
      text-transform: uppercase; color: ${MUTED};
      border-bottom: 1.5px solid #e2e8f0;
    }
    .dh-table-card .ant-table-tbody > tr:hover > td {
      background: #f0fdfa !important;
    }

    /* ── Avatar circle ── */
    .dh-avatar {
      width: 36px; height: 36px; border-radius: 50%;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-weight: 700; font-size: 13px;
      display: inline-flex; align-items: center; justify-content: center;
      margin-right: 10px; flex-shrink: 0;
    }

    /* ── Calendar modal ── */
    .dh-cal-legend {
      display: flex; flex-wrap: wrap; gap: 12px; margin-top: 16px; padding-top: 14px;
      border-top: 1px solid #e2e8f0;
    }
    .dh-cal-legend span { font-size: 12px; display: flex; align-items: center; gap: 5px; }

    /* ── Status tags override ── */
    .dh-tag {
      border-radius: 20px !important; font-size: 11px !important;
      font-weight: 600 !important; padding: 1px 10px !important; border: none !important;
    }

    /* ── Fade-in animations ── */
    @keyframes dhFadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .dh-fade-1 { animation: dhFadeUp 0.45s ease both 0.05s; }
    .dh-fade-2 { animation: dhFadeUp 0.45s ease both 0.12s; }
    .dh-fade-3 { animation: dhFadeUp 0.45s ease both 0.20s; }

    /* ── Quick summary bar ── */
    .dh-summary-bar {
      display: flex; gap: 24px; flex-wrap: wrap;
      padding: 16px 20px; background: ${TEAL_LIGHT};
      border-radius: 12px; margin-bottom: 28px;
      border: 1.5px solid #99f6e4;
    }
    .dh-summary-item { display: flex; flex-direction: column; }
    .dh-summary-label { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: ${EMERALD}; }
    .dh-summary-value { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; color: ${SLATE}; line-height: 1.1; }
    .dh-summary-divider { width: 1px; background: #99f6e4; align-self: stretch; }

    ul.events { list-style: none; margin: 0; padding: 0; }
    ul.events li { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; }
  `;
  document.head.appendChild(el);
};

/* ── Status helpers ─────────────────────────────────────────── */
const getStatusType = (status) => {
  switch (status) {
    case "approved":  return "success";
    case "pending":   return "warning";
    case "completed": return "processing";
    case "rejected":  return "error";
    default:          return "default";
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "approved":  return "blue";
    case "pending":   return "orange";
    case "completed": return "green";
    case "rejected":  return "red";
    default:          return "default";
  }
};

const initials = (name = "") =>
  name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "P";

/* ── Stat card config ─────────────────────────────────────────── */
const buildStats = (raw) => [
  {
    title: "Total",
    value: raw.totalAppointments,
    color: EMERALD,
    bg: TEAL_LIGHT,
    icon: <CalendarOutlined />,
  },
  {
    title: "Pending",
    value: raw.pendingApprovals,
    color: raw.pendingApprovals ? "#f59e0b" : EMERALD,
    bg: raw.pendingApprovals ? "#fef3c7" : TEAL_LIGHT,
    icon: raw.pendingApprovals ? <ArrowUpOutlined /> : <ArrowDownOutlined />,
  },
  {
    title: "Approved",
    value: raw.approvedAppointments,
    color: EMERALD,
    bg: TEAL_LIGHT,
    icon: <ArrowUpOutlined />,
  },
  {
    title: "Completed",
    value: raw.completedAppointments,
    color: "#3b82f6",
    bg: "#dbeafe",
    icon: <CheckCircleOutlined />,
  },
  {
    title: "Follow-ups",
    value: raw.followupAppointments,
    color: "#8b5cf6",
    bg: "#ede9fe",
    icon: <ClockCircleOutlined />,
  },
  {
    title: "Rejected",
    value: raw.totalRejected,
    color: "#ef4444",
    bg: "#fee2e2",
    icon: <CloseCircleOutlined />,
  },
];

/* ── Component ──────────────────────────────────────────────── */
function DoctorHome() {
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState([]);
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [sortedInfo, setSortedInfo] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(showLoading());
        const token = "Bearer " + localStorage.getItem("token");
        const [apptRes, statsRes, calendarRes] = await Promise.all([
          axios.get("/api/doctor/get-appointments-by-doctor-id", { headers: { Authorization: token } }),
          axios.get("/api/doctor/dashboard-stats",              { headers: { Authorization: token } }),
          axios.get("/api/doctor/get-calendar-appointments",   { headers: { Authorization: token } }),
        ]);
        if (apptRes.data.success)    setAppointments(apptRes.data.data);
        if (statsRes.data.success)   setStats(buildStats(statsRes.data.data));
        if (calendarRes.data.success) setCalendarData(calendarRes.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        dispatch(hideLoading());
      }
    };
    fetchData();
  }, [dispatch]);

  const dateCellRender = (value) => {
    const listData = calendarData.filter(
      (item) => item.date === value.format("YYYY-MM-DD")
    );
    return (
      <ul className="events">
        {listData.map((item) => (
          <li key={item._id}>
            <Badge
              status={getStatusType(item.status)}
              text={`${item.time} – ${item.userInfo?.name || "Patient"}`}
            />
          </li>
        ))}
      </ul>
    );
  };

  /* Compute quick-bar totals once stats are available */
  const totalAppts  = stats.find((s) => s.title === "Total")?.value ?? "–";
  const pendingAppts = stats.find((s) => s.title === "Pending")?.value ?? "–";
  const todayCount  = calendarData.filter(
    (a) => a.date === moment().format("YYYY-MM-DD")
  ).length;

  return (
    <Layout>
      <div className="dh-root">

        {/* ── Hero banner ── */}
        <div className="dh-hero dh-fade-1">
          <div className="dh-hero-blob" />
          <div className="dh-hero-blob-2" />
          <p className="dh-hero-sub" style={{ marginBottom: 6, opacity: 0.7, fontSize: 12 }}>
            {moment().format("dddd, D MMMM YYYY")}
          </p>
          <h1 className="dh-hero-title">Welcome back, Doctor 👋</h1>
          <p className="dh-hero-sub">
            Here's a live snapshot of your appointments and patient activity.
          </p>
          <div className="dh-hero-actions">
            <button
              className="dh-hero-btn dh-hero-btn-primary"
              onClick={() => setIsCalendarVisible(true)}
            >
              <CalendarOutlined /> View calendar
            </button>
            <button
              className="dh-hero-btn dh-hero-btn-ghost"
              onClick={() => navigate("/doctor/appointments")}
            >
              <UnorderedListOutlined /> All appointments
            </button>
          </div>
        </div>

        {/* ── Quick summary bar ── */}
        <div className="dh-summary-bar dh-fade-2">
          <div className="dh-summary-item">
            <span className="dh-summary-label">Today's appointments</span>
            <span className="dh-summary-value">{todayCount}</span>
          </div>
          <div className="dh-summary-divider" />
          <div className="dh-summary-item">
            <span className="dh-summary-label">Total all time</span>
            <span className="dh-summary-value">{totalAppts}</span>
          </div>
          <div className="dh-summary-divider" />
          <div className="dh-summary-item">
            <span className="dh-summary-label">Awaiting approval</span>
            <span
              className="dh-summary-value"
              style={{ color: pendingAppts > 0 ? "#f59e0b" : EMERALD }}
            >
              {pendingAppts}
            </span>
          </div>
        </div>

        {/* ── Stat cards ── */}
        <div className="dh-fade-2" style={{ marginBottom: 32 }}>
          <span className="dh-eyebrow">Overview</span>
          <Row gutter={[14, 14]}>
            {stats.map((stat, idx) => (
              <Col key={idx} xs={12} sm={8} md={8} lg={4}>
                <Card
                  className="dh-stat-card"
                  bodyStyle={{ padding: "18px 20px" }}
                  bordered={false}
                  hoverable
                >
                  <div
                    className="dh-stat-icon"
                    style={{ background: stat.bg, color: stat.color }}
                  >
                    {stat.icon}
                  </div>
                  <Statistic
                    title={
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          color: MUTED,
                        }}
                      >
                        {stat.title}
                      </span>
                    }
                    value={stat.value}
                    precision={0}
                    valueStyle={{
                      color: stat.color,
                      fontWeight: 700,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 28,
                      lineHeight: 1.1,
                    }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* ── Recent appointments table ── */}
        <div className="dh-fade-3">
          <span className="dh-eyebrow">Recent activity</span>
          <Card
            className="dh-table-card"
            title={
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <UserOutlined style={{ color: EMERALD }} />
                Recent patient appointments
              </span>
            }
            bordered={false}
            extra={
              <Button
                type="link"
                style={{ color: EMERALD, fontWeight: 600, padding: 0 }}
                icon={<UnorderedListOutlined />}
                onClick={() => navigate("/doctor/appointments")}
              >
                View all
              </Button>
            }
          >
            <Table
              dataSource={appointments.slice(0, 5)}
              onChange={(_, s) => setSortedInfo(s)}
              rowKey="_id"
              pagination={false}
              size="middle"
              columns={[
                {
                  title: "Patient",
                  key: "patient",
                  render: (_, record) => (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span className="dh-avatar">
                        {initials(record.userInfo?.name)}
                      </span>
                      <div>
                        <div style={{ fontWeight: 600, color: SLATE, fontSize: 13 }}>
                          {record.userInfo?.name || "N/A"}
                        </div>
                        <div style={{ color: MUTED, fontSize: 11 }}>
                          {record.userInfo?.phone || "No contact"}
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  title: "Booked date",
                  key: "date",
                  render: (_, record) =>
                    record.date
                      ? moment(record.date).format("D MMM YYYY")
                      : "–",
                },
                {
                  title: "Booked time",
                  key: "time",
                  render: (_, record) =>
                    record.time
                      ? moment(record.time, "HH:mm:ss").format("h:mm A")
                      : "–",
                },
                {
                  title: "Scheduled date",
                  key: "schedule_date",
                  render: (_, r) =>
                    r.schedule_date
                      ? moment(r.schedule_date).format("D MMM YYYY")
                      : (
                        <span style={{ color: MUTED, fontStyle: "italic", fontSize: 12 }}>
                          Not set
                        </span>
                      ),
                  sorter: (a, b) =>
                    moment(a.schedule_date || 0).unix() -
                    moment(b.schedule_date || 0).unix(),
                  sortOrder:
                    sortedInfo.columnKey === "schedule_date"
                      ? sortedInfo.order
                      : null,
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  render: (status) => (
                    <Tag
                      className="dh-tag"
                      color={getStatusColor(status)}
                    >
                      {status?.charAt(0).toUpperCase() + status?.slice(1)}
                    </Tag>
                  ),
                },
              ]}
            />
          </Card>
        </div>

        {/* ── Calendar modal ── */}
        <Modal
          title={
            <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, color: SLATE }}>
              <CalendarOutlined style={{ color: EMERALD, marginRight: 8 }} />
              Appointment calendar
            </span>
          }
          open={isCalendarVisible}
          onCancel={() => setIsCalendarVisible(false)}
          footer={[
            <Button
              key="close"
              onClick={() => setIsCalendarVisible(false)}
              style={{ borderRadius: 8 }}
            >
              Close
            </Button>,
          ]}
          width={820}
          styles={{ header: { borderBottom: `2px solid ${TEAL_LIGHT}` } }}
        >
          <Calendar cellRender={dateCellRender} mode="month" />
          <div className="dh-cal-legend">
            <span><Badge status="success"    text="Approved"  /></span>
            <span><Badge status="warning"    text="Pending"   /></span>
            <span><Badge status="processing" text="Completed" /></span>
            <span><Badge status="error"      text="Rejected"  /></span>
          </div>
        </Modal>

      </div>
    </Layout>
  );
}

export default DoctorHome;